/**
 * Ambient world life (doc 13 §F stage-6 addition, doc 10 §Particles) — the
 * things that move down there and are not trying to kill you.
 *
 * Three populations, all procedural, all deterministic from the map seed so a
 * screenshot of a tile looks the same tomorrow:
 *
 *   **algae**  swaying on the seabed, two frames, snapped to whole pixels
 *   **fish**   small drifters crossing the map, dim and slow
 *   **snow**   marine snow, always, in front of everything
 *
 * Every one of them obeys the same legibility rule (doc 10): ambience is never
 * as bright as gameplay and never wears the enemy's colour. An ambient fish the
 * player has to squint at to rule out is worse than no ambient fish at all —
 * these are dimmer, smaller, slower, and they never enter the Core's ring.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { TH, TW } from '../render/iso.ts';
import type { DepthPalette } from '../render/palette.ts';
import type { TextureCache } from '../render/pipeline.ts';
import { mix, PixelSurface, shade } from '../render/surface.ts';
import { Rng } from '../sim/rng.ts';
import { T_ROCK, type SimGrid } from '../sim/grid.ts';

/** Populations, tuned so the whole thing is a few hundred sprites at most. */
const ALGAE = 42;
const FISH = 7;
const SNOW = 55;
/** Doc 10: everything ambient moves at rest, in whole pixels, on two frames. */
const SWAY_MS = 900;

interface Alga {
  sprite: Sprite;
  /** Frame offset so the whole bed does not sway in unison. */
  phase: number;
  x: number;
  y: number;
}

interface Fish {
  sprite: Sprite;
  x: number;
  y: number;
  dx: number;
  dy: number;
  bob: number;
}

interface Flake {
  sprite: Sprite;
  x: number;
  y: number;
  speed: number;
  drift: number;
}

export class Ambience {
  private readonly algae: Alga[] = [];
  private readonly fish: Fish[] = [];
  private readonly snow: Flake[] = [];
  private readonly size: number;
  private readonly snowLayer: Container;
  private lastSway = -1;
  private readonly frames: Texture[];
  /** Reduced motion (doc 18 §3) stills all three without removing them. */
  still = false;

  constructor(
    grid: SimGrid,
    size: number,
    depth: DepthPalette,
    cache: TextureCache,
    body: Container,
    front: Container,
  ) {
    this.size = size;
    this.snowLayer = front;
    // one seed for the whole scene: the same map always grows the same kelp
    const rng = new Rng(size * 7919 + (depth.enemyGlow >>> 0));

    this.frames = [0, 1].map((f) => cache.get(`amb:alga:${depth.id}:${f}`, () => algaSurface(f, depth)));
    for (let k = 0; k < ALGAE; k++) {
      const i = Math.floor(rng.next() * size);
      const j = Math.floor(rng.next() * size);
      if (grid.terrain[grid.idx(i, j)] === T_ROCK) continue;
      const sprite = new Sprite(this.frames[0]);
      const x = i + rng.next() * 0.8 - 0.4;
      const y = j + rng.next() * 0.8 - 0.4;
      sprite.x = Math.round((x - y) * TW) - 4;
      sprite.y = Math.round((x + y) * TH + TH) - 13;
      sprite.zIndex = x + y + 0.2;
      body.addChild(sprite);
      this.algae.push({ sprite, phase: k % 2, x, y });
    }

    const fishTex = cache.get(`amb:fish:${depth.id}`, () => fishSurface(depth));
    for (let k = 0; k < FISH; k++) {
      const sprite = new Sprite(fishTex);
      const angle = rng.next();
      const f: Fish = {
        sprite,
        x: rng.next() * size,
        y: rng.next() * size,
        // slow: a third of a krill's pace, so nothing reads as a threat
        dx: (angle < 0.5 ? 1 : -1) * (0.004 + rng.next() * 0.004),
        dy: (rng.next() < 0.5 ? 1 : -1) * (0.002 + rng.next() * 0.003),
        bob: rng.next() * Math.PI * 2,
      };
      sprite.zIndex = 0;
      body.addChild(sprite);
      this.fish.push(f);
    }

    const flakeTex = cache.get(`amb:snow:${depth.id}`, () => flakeSurface(depth));
    for (let k = 0; k < SNOW; k++) {
      const sprite = new Sprite(flakeTex);
      const flake: Flake = {
        sprite,
        x: rng.next() * size,
        y: rng.next() * size,
        speed: 0.008 + rng.next() * 0.012,
        drift: (rng.next() - 0.5) * 0.006,
      };
      front.addChild(sprite);
      this.snow.push(flake);
    }
    this.place();
  }

  /** One step per frame. `now` is wall-clock ms; none of this touches the sim. */
  step(now: number): void {
    if (!this.still) {
      // two frames, ~1.1 Hz: a sway, not a shimmer (doc 10 §Animation)
      const frame = Math.floor(now / SWAY_MS) % 2;
      if (frame !== this.lastSway) {
        this.lastSway = frame;
        for (const a of this.algae) a.sprite.texture = this.frames[(frame + a.phase) % 2]!;
      }
      for (const f of this.fish) {
        f.x += f.dx;
        f.y += f.dy;
        f.bob += 0.03;
        if (f.x < -2 || f.x > this.size + 2) f.dx = -f.dx;
        if (f.y < -2 || f.y > this.size + 2) f.dy = -f.dy;
      }
      for (const s of this.snow) {
        s.y += s.speed;
        s.x += s.drift;
        if (s.y > this.size + 1) {
          s.y = -1;
          s.x = ((s.x % this.size) + this.size) % this.size;
        }
      }
    }
    this.place();
  }

  private place(): void {
    for (const f of this.fish) {
      const bob = Math.round(Math.sin(f.bob) * 2); // whole pixels only
      f.sprite.x = Math.round((f.x - f.y) * TW) - 3;
      f.sprite.y = Math.round((f.x + f.y) * TH + TH) - 10 + bob;
      f.sprite.zIndex = f.x + f.y + 0.1;
    }
    for (const s of this.snow) {
      s.sprite.x = Math.round((s.x - s.y) * TW);
      s.sprite.y = Math.round((s.x + s.y) * TH + TH) - 20;
    }
  }

  destroy(): void {
    for (const a of this.algae) a.sprite.destroy();
    for (const f of this.fish) f.sprite.destroy();
    for (const s of this.snow) s.sprite.destroy();
    this.snowLayer.removeChildren();
  }
}

/**
 * A frond: three stalks of different heights, the tip leaning one way or the
 * other. Two frames is the whole animation — doc 10 forbids a smooth wobble.
 */
export function algaSurface(frame: number, depth: DepthPalette): PixelSurface {
  const s = new PixelSurface(9, 14);
  const base = mix(depth.terrain, depth.playerGlow, 0.10);
  const tip = shade(base, 1.2);
  const lean = frame === 0 ? 1 : -1;
  for (const [x0, h] of [
    [2, 12],
    [4, 9],
    [6, 13],
  ] as const) {
    for (let k = 0; k < h; k++) {
      const bend = k > h - 5 ? lean : 0;
      s.px(x0 + bend, 13 - k, k > h - 4 ? tip : base);
    }
  }
  return s.resetOps();
}

/** A drifter: five pixels, dim, and never the enemy's colour (doc 10). */
export function fishSurface(depth: DepthPalette): PixelSurface {
  const s = new PixelSurface(7, 4);
  const body = mix(depth.waterTop, depth.playerGlow, 0.25);
  s.px(1, 1, body);
  s.px(2, 1, body);
  s.px(3, 1, body);
  s.px(4, 2, body);
  s.px(5, 1, shade(body, 0.7));
  return s.resetOps();
}

/** Marine snow: one pixel, two brightnesses, always falling (doc 10). */
export function flakeSurface(depth: DepthPalette): PixelSurface {
  const s = new PixelSurface(1, 2);
  const c = mix(depth.waterTop, depth.structureEmissive, 0.22);
  s.px(0, 0, c);
  s.px(0, 1, shade(c, 0.55));
  return s.resetOps();
}
